import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Calendar,
  FileText,
  ShoppingCart,
  Users,
  Building2,
  CalendarHeart,
  Receipt,
  Moon,
  Sun,
  LogOut,
  ClipboardCheck,
  UserCog,
  Settings,
  Palmtree,
} from 'lucide-react';
import { useTheme } from 'next-themes';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
  CommandShortcut,
} from './ui/command';
import { useAuth, UserRole } from '../contexts/AuthContext';

type CommandNavItem = {
  text: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  shortcut?: string;
};

type CommandSection = {
  heading: string;
  items: CommandNavItem[];
};

const assignSequentialShortcuts = (items: CommandNavItem[], startAt = 1): CommandNavItem[] => {
  let nextShortcut = startAt;

  return items.map((item) => {
    if (nextShortcut > 9) {
      return item;
    }

    const itemWithShortcut = { ...item, shortcut: `⌘${nextShortcut}` };
    nextShortcut += 1;
    return itemWithShortcut;
  });
};

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setTheme } = useTheme();
  const { logout, user } = useAuth();

  const runCommand = useCallback((command: () => void) => {
    setOpen(false);
    command();
  }, []);

  const navigationItemsBase: CommandNavItem[] = user?.role && [UserRole.OPERATOR, UserRole.MANAGER, UserRole.ADMIN, UserRole.ACCOUNTANT, UserRole.SUPER_ADMIN].includes(user.role)
    ? [
        { text: t('menu.dashboard'), icon: LayoutDashboard, path: '/' },
        { text: t('menu.monthlyView'), icon: Calendar, path: '/timesheet' },
        { text: t('menu.yearlySummary'), icon: FileText, path: '/yearly-summary' },
        { text: t('menu.leaveRequests', 'Leave Requests'), icon: Palmtree, path: '/leave-requests' },
      ]
    : [];

  const operationsItemsBase: CommandNavItem[] = [
    ...(user?.role && [UserRole.MANAGER, UserRole.ADMIN, UserRole.SUPER_ADMIN].includes(user.role)
      ? [{ text: t('menu.approvals', 'Approvals'), icon: ClipboardCheck, path: '/approvals' }]
      : []),
    ...(user?.role && [UserRole.MANAGER, UserRole.ADMIN, UserRole.SUPER_ADMIN].includes(user.role)
      ? [{ text: t('menu.leaveApprovals', 'Leave Approvals'), icon: ClipboardCheck, path: '/leave-approvals' }]
      : []),
    ...(user?.role && [UserRole.ADMIN, UserRole.SUPER_ADMIN].includes(user.role)
      ? [{ text: t('menu.users'), icon: Users, path: '/admin/users' }]
      : []),
    ...(user?.role === UserRole.SUPER_ADMIN
      ? [{ text: t('menu.tenantRequests', 'Tenant Requests'), icon: ClipboardCheck, path: '/admin/tenant-requests' }]
      : []),
    ...(user?.role === UserRole.SUPER_ADMIN
      ? [{ text: t('menu.tenants'), icon: Building2, path: '/admin/tenants' }]
      : []),
    ...(user?.role && [UserRole.MANAGER, UserRole.ADMIN, UserRole.SUPER_ADMIN].includes(user.role)
      ? [{ text: t('menu.nationalHolidays'), icon: CalendarHeart, path: '/national-holidays' }]
      : []),
  ];

  const navigationItems = assignSequentialShortcuts(navigationItemsBase);
  const operationsItems = assignSequentialShortcuts(operationsItemsBase, navigationItems.length + 1);

  const navigationSections: CommandSection[] = [
    {
      heading: t('commandPalette.navigation', 'Navigation'),
      items: navigationItems,
    },
    {
      heading: t('commandPalette.operations', 'Operations'),
      items: operationsItems,
    },
    {
      heading: t('commandPalette.business', 'Business'),
      items: [
        ...(user?.role && [UserRole.ACCOUNTANT, UserRole.ADMIN, UserRole.SUPER_ADMIN].includes(user.role)
          ? [{ text: t('menu.clientsSuppliers'), icon: Building2, path: '/client-supplier' }]
          : []),
        ...(user?.role && [UserRole.OPERATOR, UserRole.MANAGER, UserRole.ACCOUNTANT, UserRole.ADMIN, UserRole.SUPER_ADMIN].includes(user.role)
          ? [{ text: t('menu.expenseClaims', 'Expense Claims'), icon: Receipt, path: '/expense-claims' }]
          : []),
        ...(user?.role && [UserRole.MANAGER, UserRole.ADMIN, UserRole.SUPER_ADMIN].includes(user.role)
          ? [{ text: t('menu.orders'), icon: ShoppingCart, path: '/orders' }]
          : []),
        ...(user?.role && [UserRole.MANAGER, UserRole.ADMIN, UserRole.SUPER_ADMIN].includes(user.role)
          ? [{ text: t('menu.orderAssignments', 'Order Assignments'), icon: UserCog, path: '/order-assignments' }]
          : []),
      ],
    },
    {
      heading: t('commandPalette.settingsGroup', 'Settings'),
      items: user?.role
        ? [{ text: t('menu.settings'), icon: Settings, path: '/settings', shortcut: '⌘9' }]
        : [],
    },
  ].filter((section) => section.items.length > 0);

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder={t('commandPalette.searchPlaceholder', 'Type a command or search...')} />
      <CommandList>
        <CommandEmpty>{t('commandPalette.noResults', 'No results found.')}</CommandEmpty>

        {navigationSections.map((section, index) => (
          <React.Fragment key={section.heading}>
            {index > 0 && <CommandSeparator />}
            <CommandGroup heading={section.heading}>
              {section.items.map((item) => {
                const Icon = item.icon;
                return (
                  <CommandItem key={item.path} onSelect={() => runCommand(() => navigate(item.path))}>
                    <Icon className="mr-2 h-4 w-4" />
                    <span>{item.text}</span>
                    {item.shortcut && <CommandShortcut>{item.shortcut}</CommandShortcut>}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </React.Fragment>
        ))}

        <CommandSeparator />

        <CommandGroup heading={t('commandPalette.theme', 'Theme')}>
          <CommandItem onSelect={() => runCommand(() => setTheme('light'))}>
            <Sun className="mr-2 h-4 w-4" />
            <span>{t('commandPalette.lightMode', 'Light Mode')}</span>
          </CommandItem>
          <CommandItem onSelect={() => runCommand(() => setTheme('dark'))}>
            <Moon className="mr-2 h-4 w-4" />
            <span>{t('commandPalette.darkMode', 'Dark Mode')}</span>
          </CommandItem>
          <CommandItem onSelect={() => runCommand(() => setTheme('system'))}>
            <Sun className="mr-2 h-4 w-4" />
            <span>{t('commandPalette.systemTheme', 'System Theme')}</span>
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading={t('commandPalette.actions', 'Actions')}>
          <CommandItem onSelect={() => runCommand(logout)}>
            <LogOut className="mr-2 h-4 w-4" />
            <span>{t('menu.logout')}</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
